import type { ShipstarConfig, ShipstarInitOptions } from "./types";
import { readConfigFromScript, readConfigFromObject } from "./config";
import { fetchChangelog, fetchBlogPost, fetchBlogPosts, fetchKbArticles, fetchBanner, subscribeToChangelog } from "./api";
import { renderWidget } from "./renderer";
import { renderBlogWidget, renderBlogFeedWidget, mountBlogFeedWidget, mountBlogInteractivity } from "./blog-renderer";
import { renderKbWidget, mountKbWidget } from "./kb-renderer";
import { renderBannerWidget, mountBannerInteractivity, isBannerDismissed } from "./banner-renderer";
import { renderSkeleton, renderBlogSkeleton, renderKbSkeleton, renderBannerSkeleton } from "./skeleton";
import { widgetStyles } from "./styles";
import { debugLog } from "./utils";

(function () {
  // Guard against double-load
  if (window.Shipstar?.__loaded) return;

  // Expose public API synchronously
  window.Shipstar = {
    __loaded: true,
    init: initWidget,
    destroy: destroyWidget,
    instances: new Map(),
  };

  // Auto-init from script tag (Mode A)
  const scriptEl =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>("script[data-shipstar-key]");

  if (scriptEl?.hasAttribute("data-shipstar-key")) {
    const config = readConfigFromScript(scriptEl);
    if (config) {
      const autoMount = () => mountWidget(resolveOrCreateMountPoint(config, scriptEl), config);
      if (isTopBanner(config) && !config.containerId && !document.body) {
        // Script in <head>: the banner goes at the top of <body>, which
        // doesn't exist yet
        document.addEventListener("DOMContentLoaded", autoMount, { once: true });
      } else {
        autoMount();
      }
    }
  }

  function isTopBanner(config: ShipstarConfig): boolean {
    return config.type === "banner" && config.position === "top";
  }

  function initWidget(options: ShipstarInitOptions): void {
    if (!options.apiKey) {
      throw new Error("[Shipstar] Missing required option: apiKey");
    }

    let container: HTMLElement | null;
    if (typeof options.container === "string") {
      container = document.getElementById(options.container);
    } else {
      container = options.container;
    }

    if (!container) {
      throw new Error(
        "[Shipstar] Container element not found" +
          (typeof options.container === "string"
            ? `: #${options.container}`
            : "")
      );
    }

    if (window.Shipstar!.instances.has(container)) {
      console.warn("[Shipstar] Widget already mounted on this container.");
      return;
    }

    const config = readConfigFromObject(options);
    mountWidget(container, config);
  }

  function destroyWidget(container: HTMLElement | string): void {
    let el: HTMLElement | null;
    if (typeof container === "string") {
      el = document.getElementById(container);
    } else {
      el = container;
    }

    if (!el) {
      console.warn("[Shipstar] Container not found for destroy.");
      return;
    }

    const instance = window.Shipstar!.instances.get(el);
    if (!instance) {
      console.warn("[Shipstar] No widget instance found on this container.");
      return;
    }

    // Clear shadow DOM contents
    while (instance.shadowRoot.firstChild) {
      instance.shadowRoot.removeChild(instance.shadowRoot.firstChild);
    }
    window.Shipstar!.instances.delete(el);
  }

  function mountWidget(container: HTMLElement, config: ShipstarConfig): void {
    if (window.Shipstar!.instances.has(container)) return;

    container.setAttribute("data-theme", config.theme);

    // Reuse existing shadow root if present (e.g. after destroy + re-init)
    const shadowRoot =
      container.shadowRoot || container.attachShadow({ mode: "open" });

    const isBlog = config.type === "blog";
    const isKb = config.type === "kb";
    const isBanner = config.type === "banner";
    const skeleton = isBanner
      ? renderBannerSkeleton()
      : isKb ? renderKbSkeleton() : isBlog ? renderBlogSkeleton() : renderSkeleton();

    if (isTopBanner(config)) {
      // The host element lives outside the shadow root, so its stickiness is
      // set here: the bar stays visible while the page scrolls, and the page
      // needs no padding tricks because the bar is in flow at the top
      container.style.position = "sticky";
      container.style.top = "0";
      container.style.zIndex = "2147483000";
    }

    // Inject styles + skeleton
    shadowRoot.innerHTML = `<style>${widgetStyles}</style>${skeleton}`;

    // Track instance immediately
    window.Shipstar!.instances.set(container, { shadowRoot, config });

    debugLog(config, `Widget mounted (type=${config.type}), fetching data...`);

    const fetchPromise = isBanner
      ? fetchBanner(config).then((banner) => ({
          type: "banner" as const,
          data: banner,
        }))
      : isKb
      ? fetchKbArticles(config).then((kb) => ({
          type: "kb" as const,
          data: kb,
        }))
      : isBlog
      ? (config.apiKey === "_feed"
          ? fetchBlogPosts(config).then((posts) => ({
              type: "feed" as const,
              data: posts,
            }))
          : fetchBlogPost(config).then((post) => ({
              type: "single" as const,
              data: post,
            })))
      : fetchChangelog(config).then((changelog) => ({
          type: "changelog" as const,
          data: changelog,
        }));

    fetchPromise
      .then((result) => {
        // Keep the style tag, replace skeleton with content
        const styleTag = shadowRoot.querySelector("style");
        shadowRoot.innerHTML = "";
        if (styleTag) shadowRoot.appendChild(styleTag);

        const content = document.createElement("div");

        if (result.type === "banner") {
          if (!result.data || isBannerDismissed(result.data)) {
            // Nothing live, or the visitor closed this one: render nothing
            debugLog(config, result.data ? "Banner dismissed by visitor." : "No banner is live.");
            if (config.onLoad) config.onLoad(result.data);
            return;
          }
          content.innerHTML = renderBannerWidget(result.data, config);
          shadowRoot.appendChild(content);
          mountBannerInteractivity(shadowRoot, result.data, config);
          debugLog(config, "Widget rendered successfully.");
          if (config.onLoad) config.onLoad(result.data);
          return;
        }

        if (result.type === "kb") {
          if (config.search || config.initialArticle) {
            mountKbWidget(shadowRoot, result.data, config);
            debugLog(config, "Widget rendered successfully.");
            if (config.onLoad) config.onLoad(result.data);
            return;
          }
          content.innerHTML = renderKbWidget(result.data, config);
        } else if (result.type === "feed") {
          if (config.onNavigate) {
            mountBlogFeedWidget(shadowRoot, result.data, config);
            debugLog(config, "Widget rendered successfully.");
            if (config.onLoad) config.onLoad(result.data);
            return;
          }
          content.innerHTML = renderBlogFeedWidget(result.data, config);
        } else if (result.type === "single") {
          content.innerHTML = renderBlogWidget(result.data, config);
          // Defer interactivity mount so DOM is ready
          requestAnimationFrame(() => mountBlogInteractivity(shadowRoot, config));
        } else {
          content.innerHTML = renderWidget(result.data, config);
        }

        shadowRoot.appendChild(content);

        // Mount subscribe form for changelog widgets
        if (result.type === "changelog" && config.subscribe) {
          requestAnimationFrame(() => mountSubscribeForm(shadowRoot, config));
        }

        debugLog(config, "Widget rendered successfully.");

        if (config.onLoad) {
          config.onLoad(result.data);
        }
      })
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));

        debugLog(config, "Error loading widget:", error.message);

        const styleTag = shadowRoot.querySelector("style");
        shadowRoot.innerHTML = "";
        if (styleTag) shadowRoot.appendChild(styleTag);

        if (config.onError) {
          config.onError(error);
        }
        // A banner that can't load must not put an error box at the top of
        // the customer's page — it just stays absent
        if (isBanner) return;

        const label = isKb ? "knowledge base" : isBlog ? "blog post" : "changelog";
        const errorDiv = document.createElement("div");
        errorDiv.innerHTML = `<div class="ss-error">Unable to load ${label}.</div>`;
        shadowRoot.appendChild(errorDiv);
      });
  }

  function mountSubscribeForm(
    shadowRoot: ShadowRoot,
    config: ShipstarConfig
  ): void {
    const form = shadowRoot.querySelector<HTMLFormElement>(".ss-subscribe-form");
    if (!form) return;

    const input = form.querySelector<HTMLInputElement>(".ss-subscribe-input");
    const btn = form.querySelector<HTMLButtonElement>(".ss-subscribe-btn");
    const status = shadowRoot.querySelector<HTMLParagraphElement>(".ss-subscribe-status");
    if (!input || !btn || !status) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = input.value.trim();
      if (!email) return;

      btn.disabled = true;
      btn.textContent = "Subscribing...";
      status.textContent = "";
      status.className = "ss-subscribe-status";

      try {
        await subscribeToChangelog(config, email);
        status.textContent = "Check your email to confirm your subscription.";
        status.classList.add("ss-subscribe-success");
        input.value = "";
      } catch {
        status.textContent = "Something went wrong. Please try again.";
        status.classList.add("ss-subscribe-error");
      } finally {
        btn.disabled = false;
        btn.textContent = "Subscribe";
      }
    });
  }

  function resolveOrCreateMountPoint(
    config: ShipstarConfig,
    scriptEl: HTMLScriptElement
  ): HTMLElement {
    if (config.containerId) {
      const el = document.getElementById(config.containerId);
      if (!el) {
        throw new Error(
          `[Shipstar] Container element not found: #${config.containerId}`
        );
      }
      return el;
    }

    const div = document.createElement("div");
    if (isTopBanner(config) && document.body) {
      // A top banner belongs at the very top of the page, wherever the
      // script tag was pasted
      document.body.insertBefore(div, document.body.firstChild);
      return div;
    }
    scriptEl.parentNode!.insertBefore(div, scriptEl);
    return div;
  }
})();
